import React, { useEffect, useState } from 'react';
import 'bootstrap-select';
import "styles/global.css";
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import PrincipalService from 'service/PrincipalService';
import LoadingSpinner from 'component/LoadingSpinner';
import { hasPrincipalAccess } from '../../helpers/hasPrivilege';
import CustomDropdown from 'component/CustomDropdown';
import { PageSizeOption } from 'domain/constant/PageSizeOption';

const Principal: React.FC = () => {
  const { profile } = useSelector((state: any) => state.auth);
  const [data, setData] = useState<any[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingExportCsv, setLoadingExportCsv] = useState(false);

  const canCreate = hasPrincipalAccess(profile, 'create');
  const canUpdate = hasPrincipalAccess(profile, 'update');
  const canDelete = hasPrincipalAccess(profile, 'delete');

  const principalService = PrincipalService();

  const fetchData = async () => {
    const token = profile?.data.token;
    if (!token) {
      window.alert('Token tidak ditemukan. Silakan login kembali.');
      return;
    }

    setLoading(true);
    try {
      const res = await principalService.principalList(token, searchTerm, currentPage, pageSize);
      setData(res.data ?? []);
      setTotalPages(res.pagination?.totalPage ?? 0);
      setTotalRecords(res.pagination?.totalRecords ?? 0);
    } catch (err: any) {
      console.error(err);
      const errorMessage = err.response?.data?.message || err.message || 'Terjadi kesalahan saat mengambil data principal';
      window.alert(`Error: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [currentPage, pageSize, searchTerm]);

  const handleSearchClick = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchData();
  };

  const handleExportToCsv = async () => {
    const token = profile?.data.token;
    if (!token) {
      window.alert('Token tidak ditemukan. Silakan login kembali.');
      return;
    }

    setLoadingExportCsv(true);
    try {
      const response = await principalService.principalExportToCsv(token);
      window.alert(response?.message || 'File export sedang diproses dan akan dikirimkan ke email Anda.');
    } catch (error: any) {
      console.error("Error exporting CSV:", error);
      const errorMessage = error.response?.data?.message || error.message || error || 'Terjadi kesalahan saat export CSV';
      window.alert(`Error export CSV: ${errorMessage}`);
    } finally {
      setLoadingExportCsv(false);
    }
  };

  const handleDelete = async (id: string, name: string) => {
    if (!window.confirm(`Apakah Anda yakin ingin menghapus principal ${name}?`)) {
      return;
    }

    const token = profile?.data.token;
    if (!token) {
      window.alert('Token tidak ditemukan. Silakan login kembali.');
      return;
    }

    try {
      await principalService.principalDelete(token, id);
      window.alert(`Principal ${name} berhasil dihapus.`);
      fetchData();
    } catch (err: any) {
      console.error(err);
      const errorMessage = err.response?.data?.message || err.message || 'Terjadi kesalahan saat menghapus principal';
      window.alert(`Error: ${errorMessage}`);
    }
  };

  return (
    <div className="page-wrapper">
      <div className="row">
        <div className="col-12">
          <div className="d-flex">
            <h5 style={{ flex: 1 }}>Principals</h5>
            <div>
              <p>TransMore Principals</p>
            </div>
          </div>
        </div>
        <div className="col-12">
          <div className="p-4" style={{ background: 'white' }}>
            <div className="mb-3 d-flex flex-nowrap gap-2 overflow-auto text-nowrap">
              <form className="d-flex" onSubmit={handleSearchClick}>
                <input
                  type="text"
                  className="form-control form-control-compact me-2"
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{ minWidth: 120 }}
                />
                <button type="submit" className="px-4 btn btn-primary">
                  <i className="fa-solid fa-search"></i>
                </button>
              </form>

              <div className="d-flex justify-content-end" style={{ flex: 1 }}>
                {canCreate && (
                  <>
                    <button className="btn btn-outline-primary me-2">
                      Import From CSV
                    </button>
                  </>
                )}
                <button className="btn btn-outline-primary me-2 d-flex justify-content-center align-items-center" onClick={handleExportToCsv} disabled={loadingExportCsv}>
                  {loadingExportCsv && (
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                      aria-hidden="true"
                    />
                  )}
                  Export To CSV
                </button>
                {canCreate && (
                  <>
                    <Link to="/principal/0" className='float-end text-decoration-none tm-text-primary'>
                      <button className="btn btn-primary">
                        <span className="me-2">Add</span>
                        <i className="fa-solid fa-plus"></i>
                      </button>
                    </Link>
                  </>
                )}
              </div>
            </div>

            <table className="table table-condensed table-hover">
              <thead className="table-primary">
                <tr>
                  <th>ID</th>
                  <th>Name</th>
                  <th>Legal Name</th>
                  <th>Address</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Person In Charge</th>
                  <th>Created By</th>
                  <th>Created At</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {
                  !loading ?
                    data.length > 0 ? (
                      data.map((item) => (
                        <tr key={item.id}>
                          <td>{item.id}</td>
                          <td>{item.name}</td>
                          <td>{item.legalName}</td>
                          <td>{item.address}</td>
                          <td>{item.email}</td>
                          <td>{item.phone}</td>
                          <td>{item.personInCharge}</td>
                          <td>{item.createdBy}</td>
                          <td>{item.createdAt}</td>
                          <td>
                            <div className="d-flex">
                              {canUpdate && (
                                <Link to={'/principal/' + item.id}>
                                  <button className="border-0 btn btn-outline-warning btn-small-compact">
                                    <i className="fa-solid fa-edit"></i>
                                  </button>
                                </Link>
                              )}
                              {canDelete && (
                                <button
                                  className="border-0 btn btn-outline-danger btn-small-compact ms-2"
                                  onClick={() => handleDelete(item.id, item.name)}
                                >
                                  <i className="fa-solid fa-trash"></i>
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={10} className="text-center">
                          Tidak ada data
                        </td>
                      </tr>
                    )
                    :
                    <tr>
                      <td colSpan={10} className="text-center">
                        <div className="spinner-border text-primary" role="status">
                          <span className="visually-hidden">Loading...</span>
                        </div>
                      </td>
                    </tr>
                }
              </tbody>
            </table>
            <div className="mt-3 d-flex justify-content-end align-items-center">
              <div className="d-flex align-items-center">
                <div className="me-4 d-flex align-items-center">
                  <label className="me-2">Rows per page:</label>
                  <CustomDropdown
                    value={pageSize}
                    options={PageSizeOption.options}
                    onChange={(opt) => {
                      setPageSize(Number(opt.id));
                      setCurrentPage(1);
                      fetchData();
                    }}
                    align="top"
                  />
                </div>
                <span className="me-3">
                  {`${(currentPage - 1) * pageSize + 1}–${Math.min(currentPage * pageSize, totalRecords)} of ${totalRecords}`}
                </span>

                <nav>
                  <ul className="mb-0 pagination">
                    <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                      <button className="page-link" onClick={() => setCurrentPage(1)} style={{ border: 0 }}>
                        <i className="fa fa-angle-double-left"></i>
                      </button>
                    </li>
                    <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                      <button className="page-link" onClick={() => setCurrentPage(currentPage - 1)} style={{ border: 0 }}>
                        <i className="fa fa-angle-left"></i>
                      </button>
                    </li>

                    <li className="page-item disabled">
                      <span className="page-link" style={{ border: 0 }}>{currentPage}</span>
                    </li>

                    <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                      <button className="page-link" onClick={() => setCurrentPage(currentPage + 1)} style={{ border: 0 }}>
                        <i className="fa fa-angle-right"></i>
                      </button>
                    </li>
                    <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                      <button className="page-link" onClick={() => setCurrentPage(totalPages)} style={{ border: 0 }}>
                        <i className="fa fa-angle-double-right"></i>
                      </button>
                    </li>
                  </ul>
                </nav>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Principal;
